import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';
import { logger } from './logger';

export interface EmailTemplate {
  type: 'welcome' | 'appointment' | 'reminder' | 'document' | 'alert' | 'custom';
  subject: string;
  body: string;
  data?: Record<string, unknown>;
}

/**
 * Email Service - Using Mailgun
 * 
 * Provider: Mailgun
 * Free Tier: 5,000 emails/month for 3 months
 * Paid: $15/month for 50,000 emails
 */
const log = logger.createScope('EmailService');

export const emailService = {
  // Send welcome email to new users
  sendWelcomeEmail: async (email: string, name: string): Promise<void> => {
    try {
      log.debug('Sending welcome email', { email });
      const sendEmail = httpsCallable(functions, 'sendEmailMailgun');
      
      await sendEmail({
        to: email,
        toName: name,
        subject: 'Welcome to Suprie Pet Health! 🐕',
        text: `Hi ${name}!\n\nWelcome to Suprie Pet Health! We're excited to help you manage your pet's health records, appointments, and documents all in one place.\n\nGet started by:\n• Adding your pets\n• Uploading health records\n• Setting up appointment reminders\n• Creating unique email addresses for each pet\n\nIf you need any help, we're here for you!\n\nBest regards,\nThe Suprie Team`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
              .content { background: #f9f9f9; padding: 30px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 10px 10px; }
              .feature { background: white; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #667eea; }
              .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>🐕 Welcome to Suprie!</h1>
            </div>
            <div class="content">
              <p>Hi ${name}! 👋</p>
              <p>Welcome to <strong>Suprie Pet Health</strong>! We're excited to help you manage your pet's health records, appointments, and documents all in one place.</p>
              
              <h3>Get Started:</h3>
              <div class="feature">📝 <strong>Add Your Pets</strong> - Create profiles for all your furry friends</div>
              <div class="feature">📄 <strong>Upload Health Records</strong> - Keep all documents organized</div>
              <div class="feature">📅 <strong>Set Up Reminders</strong> - Never miss an appointment</div>
              <div class="feature">📧 <strong>Unique Email Addresses</strong> - Each pet gets their own email!</div>
              
              <p>If you need any help, we're here for you!</p>
              <p style="margin-top: 30px;">Best regards,<br><strong>The Suprie Team</strong> 🐾</p>
            </div>
            <div class="footer">
              <p>Suprie Pet Health - Managing your pet's health, one appointment at a time.</p>
            </div>
          </body>
          </html>
        `
      });
      
      log.info('Welcome email sent successfully', { email });
    } catch (error) {
      log.error('Error sending welcome email', error as Error, { email });
      throw error;
    }
  },

  // Send appointment reminder
  sendAppointmentReminder: async (email: string, data: {
    petName: string;
    date: string;
    time: string;
    type: string;
    vetName: string;
    clinicName?: string;
    location?: string;
    phone?: string;
  }): Promise<void> => {
    try {
      log.debug('Sending appointment reminder', { email, petName: data.petName, date: data.date });
      
      const sendAppointmentReminder = httpsCallable(functions, 'sendAppointmentReminderMailgun');
      await sendAppointmentReminder({
        ownerEmail: email,
        petName: data.petName,
        appointmentDate: data.date,
        appointmentTime: data.time,
        appointmentType: data.type,
        vetName: data.vetName,
        clinicName: data.clinicName || 'Veterinary Clinic',
        location: data.location,
        phone: data.phone
      });
      
      log.info('Appointment reminder sent successfully', { email, petName: data.petName });
    } catch (error) {
      log.error('Error sending appointment reminder', error as Error, { email, petName: data.petName });
      throw error;
    }
  },

  // Send document notification
  sendDocumentNotification: async (
    email: string,
    documentData: {
      petName: string;
      documentType: string;
      documentName: string;
    }
  ): Promise<void> => {
    try {
      log.debug('Sending document notification', { email, petName: documentData.petName, documentType: documentData.documentType });
      
      const sendEmail = httpsCallable(functions, 'sendEmailMailgun');
      await sendEmail({
        to: email,
        subject: `📄 New Document: ${documentData.documentName} for ${documentData.petName}`,
        text: `Hi!\n\nA new ${documentData.documentType} document has been added for ${documentData.petName}.\n\nDocument: ${documentData.documentName}\n\nYou can view it in your Suprie app.\n\nBest regards,\nSuprie Pet Health`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
              .content { background: #f9f9f9; padding: 30px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 10px 10px; }
              .doc-info { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea; }
              .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>📄 New Document</h1>
            </div>
            <div class="content">
              <p>Hi! 👋</p>
              <p>A new <strong>${documentData.documentType}</strong> document has been added for <strong>${documentData.petName}</strong>.</p>
              <div class="doc-info">
                <p><strong>Document:</strong> ${documentData.documentName}</p>
                <p><strong>Type:</strong> ${documentData.documentType}</p>
                <p><strong>Pet:</strong> ${documentData.petName}</p>
              </div>
              <p>You can view it in your Suprie app.</p>
              <p style="margin-top: 30px;">Best regards,<br><strong>Suprie Pet Health</strong> 🐾</p>
            </div>
            <div class="footer">
              <p>Suprie Pet Health - Managing your pet's health, one appointment at a time.</p>
            </div>
          </body>
          </html>
        `
      });
      
      log.info('Document notification sent successfully', { email, petName: documentData.petName });
    } catch (error) {
      log.error('Error sending document notification', error as Error, { email, petName: documentData.petName });
      throw error;
    }
  },

  // Send health alert
  sendHealthAlert: async (email: string, data: {
    petName: string;
    alertType: string;
    message: string;
    severity: string;
  }): Promise<void> => {
    try {
      log.debug('Sending health alert', { email, petName: data.petName, alertType: data.alertType, severity: data.severity });
      
      const severityEmoji = data.severity === 'high' ? '🚨' : data.severity === 'medium' ? '⚠️' : 'ℹ️';
      const severityColor = data.severity === 'high' ? '#f44336' : data.severity === 'medium' ? '#ff9800' : '#2196f3';
      
      const sendEmail = httpsCallable(functions, 'sendEmailMailgun');
      await sendEmail({
        to: email,
        subject: `${severityEmoji} Health Alert: ${data.alertType} for ${data.petName}`,
        text: `Health Alert for ${data.petName}\n\nAlert Type: ${data.alertType}\nSeverity: ${data.severity.toUpperCase()}\n\n${data.message}\n\nPlease check your Suprie app for more details.\n\nBest regards,\nSuprie Pet Health`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: ${severityColor}; color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
              .content { background: #f9f9f9; padding: 30px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 10px 10px; }
              .alert-box { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid ${severityColor}; }
              .severity { background: ${severityColor}; color: white; padding: 5px 15px; border-radius: 20px; display: inline-block; }
              .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${severityEmoji} Health Alert</h1>
            </div>
            <div class="content">
              <p><strong>Alert for ${data.petName}</strong></p>
              <div class="alert-box">
                <p><strong>Alert Type:</strong> ${data.alertType}</p>
                <p><strong>Severity:</strong> <span class="severity">${data.severity.toUpperCase()}</span></p>
                <p style="margin-top: 15px;">${data.message}</p>
              </div>
              <p>Please check your Suprie app for more details.</p>
              <p style="margin-top: 30px;">Best regards,<br><strong>Suprie Pet Health</strong> 🐾</p>
            </div>
            <div class="footer">
              <p>Suprie Pet Health - Managing your pet's health, one appointment at a time.</p>
            </div>
          </body>
          </html>
        `
      });
      
      log.info('Health alert sent successfully', { email, petName: data.petName, alertType: data.alertType });
    } catch (error) {
      log.error('Error sending health alert', error as Error, { email, petName: data.petName });
      throw error;
    }
  },

  // Send medication reminder
  sendMedicationReminder: async (email: string, data: {
    petName: string;
    medication: string;
    dosage: string;
    frequency: string;
    nextDose: string;
  }): Promise<void> => {
    try {
      log.debug('Sending medication reminder', { email, petName: data.petName, medication: data.medication });
      
      const sendEmail = httpsCallable(functions, 'sendEmailMailgun');
      await sendEmail({
        to: email,
        subject: `💊 Medication Reminder: ${data.medication} for ${data.petName}`,
        text: `Medication Reminder for ${data.petName}\n\nMedication: ${data.medication}\nDosage: ${data.dosage}\nFrequency: ${data.frequency}\nNext Dose: ${data.nextDose}\n\nDon't forget to give ${data.petName} their medication!\n\nBest regards,\nSuprie Pet Health`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
              .content { background: #f9f9f9; padding: 30px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 10px 10px; }
              .med-info { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea; }
              .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #888; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>💊 Medication Reminder</h1>
            </div>
            <div class="content">
              <p>Hi! 👋</p>
              <p>It's time to give <strong>${data.petName}</strong> their medication.</p>
              <div class="med-info">
                <p><strong>Medication:</strong> ${data.medication}</p>
                <p><strong>Dosage:</strong> ${data.dosage}</p>
                <p><strong>Frequency:</strong> ${data.frequency}</p>
                <p><strong>Next Dose:</strong> ${data.nextDose}</p>
              </div>
              <p>Don't forget to give ${data.petName} their medication!</p>
              <p style="margin-top: 30px;">Best regards,<br><strong>Suprie Pet Health</strong> 🐾</p>
            </div>
            <div class="footer">
              <p>Suprie Pet Health - Managing your pet's health, one appointment at a time.</p>
            </div>
          </body>
          </html>
        `
      });
      
      log.info('Medication reminder sent successfully', { email, petName: data.petName, medication: data.medication });
    } catch (error) {
      log.error('Error sending medication reminder', error as Error, { email, petName: data.petName });
      throw error;
    }
  },

  // Send custom email
  sendEmail: async (to: string, subject: string, body: string, html?: string, from?: string, fromName?: string, petId?: string): Promise<void> => {
    try {
      log.debug('Sending custom email', { to, subject, from, petId });
      
      // Get Firebase Auth token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated to send emails');
      }
      
      const idToken = await user.getIdToken();
      
      // Prepare email data
      const emailData: Record<string, unknown> = {
        to,
        subject,
        text: body,
        html: html || body
      };
      
      // Add from address if provided
      if (from) {
        emailData.from = from;
        if (fromName) {
          emailData.fromName = fromName;
        }
      }
      
      // Add petId if provided
      if (petId) {
        emailData.petId = petId;
      }
      
      // Call HTTP function
      const response = await fetch('https://us-central1-petvitalhub-b11c4.cloudfunctions.net/sendEmailMailgunHTTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(emailData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send email');
      }
      
      await response.json();
      log.info('Email sent successfully', { to, subject });
    } catch (error) {
      log.error('Error sending email', error as Error, { to, subject });
      throw error;
    }
  },

  // Test Mailgun integration
  testMailgun: async (testEmail?: string): Promise<unknown> => {
    try {
      log.debug('Testing Mailgun integration');
      
      const test = httpsCallable(functions, 'testMailgunEmail');
      const result = await test({ testEmail });
      
      log.info('Mailgun test successful');
      return result.data;
    } catch (error) {
      log.error('Mailgun test failed', error as Error);
      throw error;
    }
  },

  // Get emails (placeholder)
  getEmails: async (folder: string = 'inbox', petId?: string): Promise<unknown[]> => {
    log.debug('Getting emails', { folder, petId });
    // In a real application, this would make an API call to your email service
    return Promise.resolve([]);
  },

  // Mark email as read (placeholder)
  markAsRead: async (emailId: string): Promise<void> => {
    log.debug('Marking email as read', { emailId });
    // In a real application, this would make an API call to your email service
    return Promise.resolve();
  },

  // Move email to folder (placeholder)
  moveToFolder: async (emailId: string, targetFolder: string): Promise<void> => {
    log.debug('Moving email to folder', { emailId, targetFolder });
    // In a real application, this would make an API call to your email service
    return Promise.resolve();
  },

  // Delete email (placeholder)
  deleteEmail: async (emailId: string): Promise<void> => {
    log.debug('Deleting email', { emailId });
    // In a real application, this would make an API call to your email service
    return Promise.resolve();
  }
};
